import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  GlobalMessageService, GlobalMessageType, NotificationType,
  PageMetaService, PaginationModel,
  Product,
  ProductInterestEntryRelation,
  ProductInterestSearchResult,
  ProductScope, ProductService,
  TranslationService,
  UserInterestsService
} from '@spartacus/core';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { ICON_TYPE } from '../../../../../custom/icons/icon.model';

interface ProductInterestSearchResultUI extends ProductInterestSearchResult {
  results?: (ProductInterestEntryRelation & {
    product$?: Observable<Product | undefined>;
  })[];
}

@Component({
  selector: 'app-custom-notify-me',
  templateUrl: './custom-notify-me.component.html',
  styleUrls: ['./custom-notify-me.component.scss'],
  standalone: false
})
export class CustomNotifyMeComponent implements OnInit, OnDestroy {
  protected subscriptions: Subscription = new Subscription();
  iconTypes = ICON_TYPE;
  pageTitle: string;
  pageDescription: string;
  private DEFAULT_PAGE_SIZE = 10;
  private sortMapping: { [key: string]: string } = {
    byNameAsc: 'name:asc',
    byNameDesc: 'name:desc',
  };
  private sortChanged = false;

  sort = 'byNameAsc';
  sortOptions = [
    {
      code: 'byNameAsc',
      selected: false,
    },
    {
      code: 'byNameDesc',
      selected: false,
    },
  ];
  pagination: PaginationModel;

  interests$: Observable<ProductInterestSearchResultUI>;
  isRemoveDisabled$: Observable<boolean>;
  getInterestsloading$: Observable<boolean>;
  sortLabels: Observable<{ byNameAsc: string; byNameDesc: string }>;


  constructor(
    protected productInterestServiceChild: UserInterestsService,
    protected translationServiceChild: TranslationService,
    protected productServiceChild: ProductService,
    protected pageMetaService: PageMetaService,
    protected globalMessageService: GlobalMessageService
  ) {

  }

  removeProductOfNotifyMe(product: string): void {
    this.productInterestServiceChild.removeProdutInterest(
      {
        product: {
          code: product,
        },
        productInterestEntry: [
          {
            interestType: NotificationType.BACK_IN_STOCK,
          },
        ],
      }
    );
  }

  ngOnInit(): void {
    this.interests$ = this.productInterestServiceChild
      .getAndLoadProductInterests(this.DEFAULT_PAGE_SIZE)
      .pipe(
        tap(
          (interests) =>
            (this.pagination = {
              currentPage: interests.pagination?.page,
              pageSize: interests.pagination?.count,
              totalPages: interests.pagination?.totalPages,
              totalResults: interests.pagination?.totalCount,
              sort: 'byNameAsc',
            })
        ),
        tap(() => {
          if (this.sortChanged) {
            this.sortChanged = false;
            this.globalMessageService?.add(
              { key: 'sorting.pageViewUpdated' },
              GlobalMessageType.MSG_TYPE_ASSISTIVE,
              500
            );
          }
        }),
        map((interest) => ({
          ...interest,
          results: interest.results
            ? interest.results.map((result) => ({
              ...result,
              product$: this.getProduct(result),
            }))
            : interest.results,
        }))
      );

    this.getInterestsloading$ =
      this.productInterestServiceChild.getProdutInterestsLoading();
    this.isRemoveDisabled$ = combineLatest([
      this.getInterestsloading$,
      this.productInterestServiceChild.getRemoveProdutInterestLoading(),
    ]).pipe(map(([getLoading, removeLoading]) => getLoading || removeLoading));

    this.sortLabels = this.getSortLabels();

    this.subscriptions.add(
      this.pageMetaService.getMeta().subscribe(
        data => {
          this.pageTitle = data.title;
          this.pageDescription = data.description;
        },
      ),
    );
  }


  private getSortLabels(): Observable<{
    byNameAsc: string;
    byNameDesc: string;
  }> {
    return combineLatest([
      this.translationServiceChild.translate('myInterests.sorting.byNameAsc'),
      this.translationServiceChild.translate('myInterests.sorting.byNameDesc'),
    ]).pipe(
      map(([asc, desc]) => {
        return {
          byNameAsc: asc,
          byNameDesc: desc,
        };
      })
    );
  }

  private getProduct(
    interest: ProductInterestEntryRelation
  ): Observable<Product | undefined> {
    return this.productServiceChild.get(
      interest.product?.code ?? '',
      ProductScope.DETAILS
    );
  }

  removeInterest(
    relation: ProductInterestEntryRelation & {
      product$?: Observable<Product | undefined>;
    }
  ): void {
    this.productInterestServiceChild.removeProdutInterest({
      product: relation.product,
      productInterestEntry: relation.productInterestEntry,
    });
  }

  sortChange(sort: string): void {
    this.sort = sort;
    this.sortChanged = true;
    this.productInterestServiceChild.loadProductInterests(
      this.DEFAULT_PAGE_SIZE,
      0,
      this.sortMapping[sort]
    );
  }

  pageChange(page: number): void {
    this.productInterestServiceChild.loadProductInterests(
      this.DEFAULT_PAGE_SIZE,
      page,
      this.sortMapping[this.sort]
    );
  }


  ngOnDestroy(): void {
    this.productInterestServiceChild.clearProductInterests();
    this.productInterestServiceChild.resetRemoveInterestState();
    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }
  }
}
